import { Injectable, inject } from '@angular/core';
import { formatDate } from '@angular/common';

import { Locale } from '../locale/locale.domain';
import { LocaleService } from '../locale/locale.service';

export enum DateTimeFormat {
  DAY = 'DAY',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  DAY_OF_WEEK_FULL = 'DAY_OF_WEEK_FULL',
  DAY_DATE_MONTH = 'DAY_DATE_MONTH',
  DATE = 'DATE',
  DATE_TIME = 'DATE_TIME',
  DATE_MONTH = 'DATE_MONTH',
  DATE_MONTH_FULL = 'DATE_MONTH_FULL',
  TIME = 'TIME',
  YEAR = 'YEAR',
  YEAR_MONTH = 'YEAR_MONTH',
  YEAR_MONTH_DAY = 'YEAR_MONTH_DAY',
  HOUR_MINUTE = 'HOUR_MINUTE',
  MONTH = 'MONTH',
  MONTH_FULL = 'MONTH_FULL',
  MONTH_DAY = 'MONTH_DAY',
  MONTH_DATE = 'MONTH_DATE',
  MONTH_YEAR = 'MONTH_YEAR',
  MONTH_YEAR_ABBR = 'MONTH_YEAR_ABBR',
  MONTH_YEAR_FULL = 'MONTH_YEAR_FULL',
  MONTH_DAY_YEAR = 'MONTH_DAY_YEAR',
  MONTH_DAY_YEAR_ALT = 'MONTH_DAY_YEAR_ALT',
  LONG_DATE = 'LONG_DATE',
  DATE_ALT = 'DATE_ALT',
  TIME_SHORT = 'TIME_SHORT',
  TIME_SHORT_WITH_ZONE = 'TIME_SHORT_WITH_ZONE',
  DAY_DATE = 'DAY_DATE',
  ISO_LOCAL_DATE_TIME = 'ISO_LOCAL_DATE_TIME',
  ISO_LOCAL_DATE_TIME_TZ = 'ISO_LOCAL_DATE_TIME_TZ',
  DAY_MONTH_DATE = 'DAY_MONTH_DATE',
  DAY_MONTH_DATE_YEAR = 'DAY_MONTH_DATE_YEAR'
}

/**
 * DAY = 'dd'
 *
 * DAY_OF_WEEK = 'EEE'
 *
 * DAY_OF_WEEK_FULL = 'EEEE'
 *
 * DAY_DATE_MONTH = 'EEE d MMMM'
 *
 * DATE = 'dd MMM yyyy'
 *
 * DATE_TIME = 'dd MMM yyyy, HH:mm:ss \'HE\''
 *
 * DATE_MONTH = 'dd MMM'
 *
 * DATE_MONTH_FULL = 'dd MMMM'
 *
 * TIME = 'HH:mm:ss \'HE\''
 *
 * YEAR = 'yyyy'
 *
 * YEAR_MONTH = 'yyyy-MMM'
 *
 * YEAR_MONTH_DAY = 'yyyy-MM-dd'
 *
 * HOUR_MINUTE = 'HH:mm'
 *
 * MONTH = 'MMM'
 *
 * MONTH_FULL = 'MMMM',
 *
 * MONTH_DAY = 'MMdd'
 *
 * MONTH_DATE = 'MMM dd'
 *
 * MONTH_YEAR = 'MM-yyyy'
 *
 * MONTH_YEAR_ABBR = 'MMM yyyy'
 *
 * MONTH_YEAR_FULL = 'MMMM yyyy'
 *
 * MONTH_DAY_YEAR = 'MMMM d, yyyy'
 *
 * LONG_DATE = 'dd MMMM yyyy'
 *
 * DATE_ALT = 'MMM dd, yyyy'
 *
 * TIME_SHORT = 'HH:mm'
 *
 * TIME_SHORT_WITH_ZONE = 'HH:mm \'HE\''
 *
 * DAY_DATE = 'EEE MMM dd, yyyy'
 *
 * ISO_LOCAL_DATE_TIME = 'yyyy-MM-ddTHH:mm:ss'
 *
 * ISO_LOCAL_DATE_TIME_TZ = 'yyyy-MM-ddTHH:mm:ssZ'
 */
export type DateTimeFormatType = keyof typeof DateTimeFormat;

/**
 * Subtype of DateTimeFormat for date-picker
 */
export type DatePickerFormatType = keyof Pick<typeof DateTimeFormat,
  'DAY' |
  'DAY_OF_WEEK' |
  'DAY_OF_WEEK_FULL' |
  'YEAR_MONTH_DAY' |
  'MONTH_DAY' |
  'MONTH_FULL' |
  'MONTH_YEAR' |
  'MONTH_YEAR_FULL' |
  'MONTH_DAY_YEAR' |
  'YEAR'>

class FormatPlaceholder {
  get placeholder(): string {
    return this._placeholder ?? this._format;
  }

  get format(): string {
    return this._format;
  }

  private _format: string;
  private _placeholder: string;

  constructor(format: string, placeholder?: string) {
    this._format = format;
    if (placeholder) {
      this._placeholder = placeholder;
    } else {
      this._placeholder = format;
    }
  }
}

/**
 * Delegates to Angular's formatDate but operates with predefined list of locale specific formats
 */
@Injectable({
  providedIn: 'root'
})
export class DateFormatterService {
  private readonly localeService = inject<LocaleService>(LocaleService);

  private static readonly DEFAULT_LOCALE: Locale = Locale.en_CA;

  private static readonly DATE_FORMAT_MAP: Map<Locale, Map<DateTimeFormatType, FormatPlaceholder>> = new Map<Locale, Map<DateTimeFormatType, FormatPlaceholder>>(
    [
      [Locale.fr_CA, new Map<DateTimeFormatType, FormatPlaceholder>([
        ['DAY', new FormatPlaceholder('dd')],
        ['DAY_OF_WEEK', new FormatPlaceholder('EEE')],
        ['DAY_OF_WEEK_FULL', new FormatPlaceholder('EEEE')],
        ['DAY_DATE_MONTH', new FormatPlaceholder('EEE d MMMM')],
        ['DATE', new FormatPlaceholder('dd MMM yyyy')],
        ['DATE_TIME', new FormatPlaceholder('dd MMM yyyy, HH:mm:ss \'HE\'')],
        ['DATE_MONTH', new FormatPlaceholder('dd MMM')],
        ['DATE_MONTH_FULL', new FormatPlaceholder('dd MMMM')],
        ['TIME', new FormatPlaceholder('HH:mm:ss \'HE\'')],
        ['YEAR', new FormatPlaceholder('yyyy')],
        ['YEAR_MONTH', new FormatPlaceholder('yyyy-MMM')], // i.e. 2014-avr
        ['YEAR_MONTH_DAY', new FormatPlaceholder('yyyy-MM-dd', 'AAAA-MM-JJ')],
        ['HOUR_MINUTE', new FormatPlaceholder('HH:mm')],
        ['MONTH', new FormatPlaceholder('MMM')],
        ['MONTH_FULL', new FormatPlaceholder('MMMM')],
        ['MONTH_DAY', new FormatPlaceholder('MMdd', 'MMJJ')],
        ['MONTH_DATE', new FormatPlaceholder('MMM dd')],
        ['MONTH_YEAR', new FormatPlaceholder('MM-yyyy', 'MM-AAAA')],
        ['MONTH_YEAR_ABBR', new FormatPlaceholder('MMM yyyy')],
        ['MONTH_YEAR_FULL', new FormatPlaceholder('MMMM yyyy')],
        ['MONTH_DAY_YEAR', new FormatPlaceholder('MMMM d, yyyy', 'MMMM J, AAAA')],
        ['MONTH_DAY_YEAR_ALT', new FormatPlaceholder('MM-dd-yyyy')],
        ['LONG_DATE', new FormatPlaceholder('dd MMMM yyyy')],
        ['DATE_ALT', new FormatPlaceholder('MMM dd, yyyy')],
        ['TIME_SHORT', new FormatPlaceholder('HH:mm')],
        ['TIME_SHORT_WITH_ZONE', new FormatPlaceholder('HH:mm \'HE\'')],
        ['DAY_DATE', new FormatPlaceholder('EEE MMM dd, yyyy')],
        ['ISO_LOCAL_DATE_TIME', new FormatPlaceholder('yyyy-MM-ddTHH:mm:ss')],
        ['ISO_LOCAL_DATE_TIME_TZ', new FormatPlaceholder('yyyy-MM-ddTHH:mm:ssZ')],
        ['DAY_MONTH_DATE', new FormatPlaceholder('EEEE, MMMM d')],
        ['DAY_MONTH_DATE_YEAR', new FormatPlaceholder('EEEE, MMMM d, yyyy')]
      ])],
      [Locale.en_CA, new Map<DateTimeFormatType, FormatPlaceholder>([
        ['DAY', new FormatPlaceholder('dd')],
        ['DAY_OF_WEEK', new FormatPlaceholder('EEE')],
        ['DAY_OF_WEEK_FULL', new FormatPlaceholder('EEEE')],
        ['DAY_DATE_MONTH', new FormatPlaceholder('EEE d MMMM')],
        ['DATE', new FormatPlaceholder('dd MMM yyyy')],
        ['DATE_TIME', new FormatPlaceholder('dd MMM yyyy, hh:mm:ss a \'ET\'')],
        ['DATE_MONTH', new FormatPlaceholder('dd MMM')],
        ['DATE_MONTH_FULL', new FormatPlaceholder('dd MMMM')],
        ['TIME', new FormatPlaceholder('hh:mm:ss a \'ET\'')],
        ['YEAR', new FormatPlaceholder('yyyy')],
        ['YEAR_MONTH', new FormatPlaceholder('yyyy-MMM')],
        ['YEAR_MONTH_DAY', new FormatPlaceholder('yyyy-MM-dd')],
        ['HOUR_MINUTE', new FormatPlaceholder('h:mm')],
        ['MONTH', new FormatPlaceholder('MMM')],
        ['MONTH_FULL', new FormatPlaceholder('MMMM')],
        ['MONTH_DAY', new FormatPlaceholder('MMdd')],
        ['MONTH_DATE', new FormatPlaceholder('MMM dd')],
        ['MONTH_YEAR', new FormatPlaceholder('MM-yyyy')],
        ['MONTH_YEAR_ABBR', new FormatPlaceholder('MMM yyyy')],
        ['MONTH_YEAR_FULL', new FormatPlaceholder('MMMM yyyy')],
        ['MONTH_DAY_YEAR', new FormatPlaceholder('MMMM d, yyyy')],
        ['MONTH_DAY_YEAR_ALT', new FormatPlaceholder('MM-dd-yyyy')],
        ['LONG_DATE', new FormatPlaceholder('dd MMMM yyyy')],
        ['DATE_ALT', new FormatPlaceholder('MMM dd, yyyy')],
        ['TIME_SHORT', new FormatPlaceholder('hh:mm a')],
        ['TIME_SHORT_WITH_ZONE', new FormatPlaceholder('hh:mm a \'ET\'')],
        ['DAY_DATE', new FormatPlaceholder('EEE MMM dd, yyyy')],
        ['ISO_LOCAL_DATE_TIME', new FormatPlaceholder('yyyy-MM-ddTHH:mm:ss')],
        ['ISO_LOCAL_DATE_TIME_TZ', new FormatPlaceholder('yyyy-MM-ddTHH:mm:ssZ')],
        ['DAY_MONTH_DATE', new FormatPlaceholder('EEEE, MMMM d')],
        ['DAY_MONTH_DATE_YEAR', new FormatPlaceholder('EEEE, MMMM d, yyyy')]
      ])]
    ]
  );

  private readonly currentLocale: Locale;

  constructor() {
    this.currentLocale = this.localeService.getLocale() ?? DateFormatterService.DEFAULT_LOCALE;
  }

  /**
   * @param date Date object to format
   * @param format one of the supported date formats
   * @returns  string yyyy-mm-ddThh:mm:ss
   *
   * @description
   * Formats data object as localized date
   */
  formatDate(date: Date, format: DateTimeFormatType): string {
    date = this.convertToDateIfPossible(date);
    if (date === undefined || date === null) {
      return '';
    }
    return formatDate(date, this.convertFormat(format), this.currentLocale);
  }

  getLocalizedFormat(format: DateTimeFormatType): string {
    return DateFormatterService.DATE_FORMAT_MAP.get(this.currentLocale).get(format).placeholder;
  }

  wrapDateWithTimeAndTZ(date: Date): string {
    return this.formatDate(new Date(this.addLocalTimeToDate(date)), DateTimeFormat.ISO_LOCAL_DATE_TIME_TZ);
  }

  addLocalTimeToDate(date: Date): Date {
    const now = new Date();
    date.setHours(now.getHours());
    date.setMinutes(now.getMinutes());
    date.setSeconds(now.getSeconds());
    return date;
  }

  private convertFormat(format: DateTimeFormatType): string {
    const formatValue: DateTimeFormatType = typeof format === 'string' ? DateTimeFormat[format] : format;
    const calculatedFormat = DateFormatterService.DATE_FORMAT_MAP.get(this.currentLocale).get(formatValue).format;
    return calculatedFormat ?? format;
  }

  private convertToDateIfPossible(value: number | Date): Date {
    return typeof value === 'number' ? new Date(value) : value;
  }
}

